import { useEffect, useRef, useState } from "react";
import {
  isTurnstileEnabled,
  loadTurnstileScript,
  resolveTurnstileSiteKey,
  type TurnstilePublicInfo,
} from "@/lib/turnstile";

type Props = {
  turnstile?: TurnstilePublicInfo | null;
  /** Siteverify action label (signup|signin|forgot|…). */
  action?: string;
  onToken: (token: string) => void;
  onExpire?: () => void;
  className?: string;
};

/**
 * Managed Turnstile widget. Renders nothing when enforcement is off / no sitekey.
 */
export function TurnstileWidget({
  turnstile,
  action,
  onToken,
  onExpire,
  className,
}: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const enabled = isTurnstileEnabled(turnstile);
  const sitekey = resolveTurnstileSiteKey(turnstile);

  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!enabled || !sitekey) {
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setError(null);
    setStatus("loading");

    const mount = () => {
      if (cancelled || !hostRef.current || !window.turnstile) return false;
      if (widgetIdRef.current) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
      hostRef.current.innerHTML = "";
      try {
        widgetIdRef.current = window.turnstile.render(hostRef.current, {
          sitekey,
          action,
          theme: "auto",
          callback: (token) => {
            onTokenRef.current(token);
          },
          "expired-callback": () => {
            onTokenRef.current("");
            onExpireRef.current?.();
          },
          "error-callback": (code) => {
            onTokenRef.current("");
            const hint =
              code === "110200" || code === "600010"
                ? " Hostname is not allowed for this Turnstile sitekey — add this portal domain in the Cloudflare Turnstile widget settings."
                : code
                  ? ` (code ${code})`
                  : "";
            setError(`Verification failed to load.${hint}`);
            setStatus("error");
          },
        });
        setStatus("ready");
        return true;
      } catch (reason: unknown) {
        setError(
          reason instanceof Error
            ? reason.message
            : "Turnstile render failed",
        );
        setStatus("error");
        return false;
      }
    };

    void loadTurnstileScript()
      .then(() => {
        if (cancelled) return;
        // hostRef is set after first paint; retry once on rAF if needed.
        if (mount()) return;
        requestAnimationFrame(() => {
          if (!cancelled) mount();
        });
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setError(
            reason instanceof Error ? reason.message : "Turnstile unavailable",
          );
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
        widgetIdRef.current = null;
      }
    };
  }, [enabled, sitekey, action]);

  if (!enabled) {
    return null;
  }

  return (
    <div
      className={["flex w-full flex-col items-center justify-center", className]
        .filter(Boolean)
        .join(" ")}
      data-testid="turnstile-widget"
      data-status={status}
    >
      {status === "loading" ? (
        <p className="mb-2 text-center text-xs text-text-muted">
          Loading verification…
        </p>
      ) : null}
      {/* Cloudflare injects the Managed widget into this host; center the iframe/checkbox. */}
      <div
        ref={hostRef}
        className="flex min-h-[65px] w-full justify-center [&_iframe]:mx-auto [&_>div]:mx-auto"
      />
      {error ? (
        <p className="mt-2 max-w-md text-center text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Reset the widget after a failed submit so a fresh token can be issued. */
export function resetTurnstileWidgets() {
  if (typeof window !== "undefined" && window.turnstile) {
    try {
      window.turnstile.reset();
    } catch {
      /* ignore */
    }
  }
}
